import React,{useState,useEffect} from 'react'
import axios from 'axios'
import moment from 'moment';
import Panel from '../ui/Panel'
import StatTile from '../ui/StatTile'
import Btn from '../ui/Btn'

/* ------------------------------------------------------------------
   The receipts endpoint proxies Stripe, and rows have reached this
   screen under both Stripe's own snake_case keys and the API's
   PascalCase ones. Nothing here changes the request or how the
   response is stored — these helpers only read whichever key a row
   happens to carry, so the table never renders "undefined".
   ------------------------------------------------------------------ */

const pick=(row,keys)=>{
  for (let i=0;i<keys.length;i++){
    const value=row[keys[i]]
    if (value!==undefined&&value!==null&&value!=='') return value
  }
  return null
}

/* Stripe reports money in the currency's smallest unit (2350 = 23.50 EUR)
   under its snake_case keys; the API's own columns are already in units.
   The key that supplied the number decides which reading applies. */
const MINOR_UNIT_KEYS=['amount','amount_paid','amount_captured','amount_total']
const MAJOR_UNIT_KEYS=['Amount','AmountPaid','Total','Price','Value']

const amountOf=(row)=>{
  for (let i=0;i<MINOR_UNIT_KEYS.length;i++){
    const raw=row[MINOR_UNIT_KEYS[i]]
    if (raw!==undefined&&raw!==null&&raw!==''){
      const n=Number(raw)
      if (!isNaN(n)) return n/100
    }
  }
  for (let i=0;i<MAJOR_UNIT_KEYS.length;i++){
    const raw=row[MAJOR_UNIT_KEYS[i]]
    if (raw!==undefined&&raw!==null&&raw!==''){
      const n=Number(raw)
      if (!isNaN(n)) return n
    }
  }
  return null
}

const currencyOf=(row)=>{
  const raw=pick(row,['currency','Currency','CurrencyCode'])
  return raw?String(raw).toUpperCase():'EUR'
}

/* Stripe timestamps are unix seconds; the API sends ISO strings. */
const dateOf=(row)=>{
  const stamp=pick(row,['created','Created'])
  if (stamp!==null&&!isNaN(Number(stamp))){
    const m=moment.unix(Number(stamp))
    if (m.isValid()) return m
  }
  const iso=pick(row,['DateCreated','date','Date','PaymentDate','PurchaseDate','created_at'])
  if (iso!==null){
    const m=moment(iso)
    if (m.isValid()) return m
  }
  return null
}

const urlOf=(row)=>pick(row,['receipt_url','ReceiptUrl','ReceiptURL','hosted_invoice_url','invoice_pdf','Url','URL'])

const referenceOf=(row)=>pick(row,['receipt_number','ReceiptNumber','number','InvoiceNumber','invoice','id','ID','ChargeID'])

const descriptionOf=(row)=>pick(row,['description','Description','ProductTitle','Title','ProductName','statement_descriptor'])

const statusOf=(row)=>{
  const raw=pick(row,['status','Status'])
  if (raw!==null) return String(raw)
  const paid=row.paid
  if (paid===true||paid===1||paid==='1') return 'paid'
  if (paid===false||paid===0||paid==='0') return 'unpaid'
  return null
}

const money=(value,currency)=>{
  if (value===null||isNaN(Number(value))) return '—'
  try{
    return new Intl.NumberFormat(undefined,{style:'currency',currency:currency,minimumFractionDigits:2}).format(Number(value))
  }catch(err){
    /* An unexpected currency code must not take the page down. */
    return Number(value).toFixed(2)+' '+currency
  }
}

const SK_CELL={display:'block',height:12,width:'80%'}

export default function Billings() {
    const [data,setData]=useState([])
    const [isLoading,setIsLoading]=useState(false)
    /* Inline UI instead of alert(): a retryable strip inside the panel. */
    const [loadError,setLoadError]=useState(false)

    useEffect(()=>{
        loadData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
    },[])


    const loadData=()=>{
        setIsLoading(true)
        axios.get(process.env.REACT_APP_API_SERVER+'manager/customersettings/stripereceipts/'+sessionStorage.getItem('userToken'),{
          headers: {
              'Authorization': 'Bearer '+sessionStorage.getItem("mngapiToken")
              }
          })
        .then(res=>{


            console.log(res.data)
            setData(res.data.results)

           setIsLoading(false)
           setLoadError(false)
        }).catch(err=>{
            setIsLoading(false)
            setLoadError(true)

        })
    }

    const rows=Array.isArray(data)?data:[]

    const receipts=rows.map((row,index)=>{
      const source=(row&&typeof row==='object')?row:{}
      const currency=currencyOf(source)
      const amount=amountOf(source)
      const when=dateOf(source)
      return {
        key:'receipt'+index,
        amount:amount,
        currency:currency,
        when:when,
        url:urlOf(source),
        reference:referenceOf(source),
        description:descriptionOf(source),
        status:statusOf(source)
      }
    })

    /* Mixed currencies would make a single sum meaningless, so the total
       is only offered when every receipt agrees on one. */
    const currencies=receipts.filter(r=>r.amount!==null).map(r=>r.currency)
    const oneCurrency=currencies.length>0&&currencies.every(c=>c===currencies[0])
    const total=oneCurrency
      ?receipts.reduce((sum,r)=>sum+(r.amount===null?0:r.amount),0)
      :null

    const dated=receipts.filter(r=>r.when).sort((a,b)=>b.when.valueOf()-a.when.valueOf())
    const latest=dated.length>0?dated[0]:null

    const ordered=receipts.slice().sort((a,b)=>{
      if (a.when&&b.when) return b.when.valueOf()-a.when.valueOf()
      if (a.when) return -1
      if (b.when) return 1
      return 0
    })

    const skeletonRows=[0,1,2,3].map(i=>(
      <tr key={'sk'+i}>
        <td><span className="skeleton" style={SK_CELL}></span></td>
        <td><span className="skeleton" style={SK_CELL}></span></td>
        <td className="n"><span className="skeleton" style={{display:'inline-block',height:12,width:64}}></span></td>
        <td><span className="skeleton" style={{display:'block',height:12,width:56}}></span></td>
        <td><span className="skeleton" style={{display:'block',height:12,width:70}}></span></td>
      </tr>
    ))

  return (
    <div className="grid">

      <section className="scoreboard" aria-label="Billing summary">

        <div className="sb-head">
          <span className="mono">Billing</span>
          <span className="sb-live">
            <span className="dot"></span>
            <span className="mono">{isLoading?'Loading':'Stripe receipts'}</span>
          </span>
        </div>

        <div className="sb-grid">
          <StatTile
            index={0}
            label="Receipts"
            value={isLoading?'—':receipts.length}
            note={receipts.length===0
              ? 'No payments have been recorded on this account yet.'
              : 'Every payment Stripe has issued a receipt for.'}
          />

          <StatTile
            index={1}
            label="Total Paid"
            value={isLoading||total===null?'—':money(total,currencies[0])}
            note={total!==null
              ? <>Across <b>{receipts.length===1?'1 payment':receipts.length+' payments'}</b>.</>
              : (currencies.length===0
                  ? 'Nothing has been charged to this account yet.'
                  : 'Receipts span more than one currency, so they are not summed.')}
          />

          <StatTile
            index={2}
            label="Last Payment"
            value={isLoading||!latest?'—':latest.when.format('DD/MM/YYYY')}
            note={latest
              ? <>{money(latest.amount,latest.currency)} — <b>{latest.when.fromNow()}</b></>
              : 'Nothing to show until the first payment clears.'}
          />
        </div>

      </section>

      <Panel
        title="Receipts"
        subtitle="Issued by Stripe. Open one for the printable copy."
        actions={<Btn onClick={loadData} disabled={isLoading}>{isLoading?'Refreshing':'Refresh'}</Btn>}
      >

        {loadError
          ?<p className="callout">
              <b>Your receipts could not be loaded.</b> The billing service did not answer. Nothing has been charged or changed — use Refresh to try again.
            </p>
          :null}

        {!loadError&&!isLoading&&ordered.length===0
          ?<div className="empty">
              No receipts yet. They appear here as soon as a payment goes through.
            </div>
          :null}

        {(isLoading||ordered.length>0)&&!loadError
          ?<div className="scroll-body">
              <table className="tbl">
                <caption>{isLoading?'Loading receipts':(ordered.length===1?'1 receipt':ordered.length+' receipts')}</caption>
                <thead>
                  <tr>
                    <th scope="col">Date</th>
                    <th scope="col">Description</th>
                    <th scope="col">Amount</th>
                    <th scope="col">Status</th>
                    <th scope="col">Receipt</th>
                  </tr>
                </thead>
                <tbody>
                  {isLoading
                    ?skeletonRows
                    :ordered.map(item=>(
                        <tr key={item.key}>
                          <td className="num">{item.when?item.when.format('DD/MM/YYYY'):'—'}</td>
                          <td>
                            {item.description?item.description:'Subscription payment'}
                            {item.reference
                              ?<div className="mono" style={{marginTop:3}}>{item.reference}</div>
                              :null}
                          </td>
                          <td className="n">{money(item.amount,item.currency)}</td>
                          <td>{item.status?item.status:'—'}</td>
                          <td>
                            {item.url
                              ?<a className="btn-ui" href={item.url} target="_blank" rel="noreferrer">View</a>
                              :<span className="mono">Not available</span>}
                          </td>
                        </tr>
                      ))}
                </tbody>
              </table>
            </div>
          :null}

      </Panel>

    </div>
  )
}
